<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Avatar from 'next/avatar/Avatar.vue';
import { emitter } from 'shared/helpers/mitt';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  assigneeId: {
    type: Number,
    default: null,
  },
});

const store = useStore();
const { t } = useI18n();
const expanded = ref(false);

const conversationKey = computed(() => Number(props.conversationId));
const assignments = computed(() =>
  store.getters['conversationAssignments/getAssignments'](conversationKey.value)
);
const meta = computed(() =>
  store.getters['conversationAssignments/getAssignmentsMeta'](conversationKey.value)
);
const uiFlags = computed(() =>
  store.getters['conversationAssignments/getUIFlags'](conversationKey.value)
);
const hasAssignments = computed(() => assignments.value.length > 0);
const currentAssignment = computed(() => assignments.value[0] || null);
const systemLabel = computed(() => t('CONVERSATION.ASSIGNMENT_HISTORY.SYSTEM'));
const unassignedLabel = computed(() => t('CONVERSATION.ASSIGNMENT_HISTORY.UNASSIGNED'));
const expectedCurrentAssignment = computed(() => {
  if (!props.assigneeId) {
    return assignments.value.find(assignment => !assignment?.assignee?.id) || null;
  }

  return (
    assignments.value.find(
      assignment => Number(assignment?.assignee?.id) === Number(props.assigneeId)
    ) || null
  );
});
const effectiveCurrentAssignment = computed(
  () => expectedCurrentAssignment.value || currentAssignment.value
);
const isInSync = computed(() => {
  if (!hasAssignments.value) {
    return true;
  }

  const headerAssigneeId = Number(props.assigneeId || 0);
  const widgetAssigneeId = Number(effectiveCurrentAssignment.value?.assignee?.id || 0);
  return headerAssigneeId === widgetAssigneeId;
});
const syncRecoveryAttempts = ref(0);
const MAX_SYNC_RECOVERY_ATTEMPTS = 3;
const shouldShowDesyncWarning = computed(() => {
  if (uiFlags.value?.isFetching) return false;
  if (syncRecoveryAttempts.value <= 0) return false;
  if (syncRecoveryAttempts.value > MAX_SYNC_RECOVERY_ATTEMPTS) return false;
  return !isInSync.value;
});
const DEBUG =
  typeof window !== 'undefined' &&
  window?.localStorage?.getItem('assignment_widget_debug') === '1';

const log = (level, message, data = {}) => {
  if (level === 'debug' && !DEBUG) return;

  const payload = {
    conversationId: conversationKey.value,
    assigneeId: props.assigneeId,
    timestamp: new Date().toISOString(),
    ...data,
  };

  if (level === 'error') console.error('[AssignmentWidget]', message, payload);
  if (level === 'warn') console.warn('[AssignmentWidget]', message, payload);
  if (level === 'info') console.info('[AssignmentWidget]', message, payload);
  if (level === 'debug') console.debug('[AssignmentWidget]', message, payload);
};

const formatType = assignmentType => {
  const types = {
    manual: t('CONVERSATION.ASSIGNMENT_HISTORY.TYPES.MANUAL'),
    auto_redistribution: t('CONVERSATION.ASSIGNMENT_HISTORY.TYPES.AUTO_REDISTRIBUTION'),
    ai_transfer: t('CONVERSATION.ASSIGNMENT_HISTORY.TYPES.AI_TRANSFER'),
    round_robin: t('CONVERSATION.ASSIGNMENT_HISTORY.TYPES.ROUND_ROBIN'),
    bot_handoff: t('CONVERSATION.ASSIGNMENT_HISTORY.TYPES.BOT_HANDOFF'),
  };
  return types[assignmentType] || assignmentType;
};

const formatDuration = seconds => {
  if (!seconds && seconds !== 0) return '';
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (!remainingSeconds) return `${minutes}min`;
  return `${minutes}min ${remainingSeconds}s`;
};

const formatTimestamp = timestamp => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const fetchAssignments = async () => {
  if (!conversationKey.value) return;
  const start = performance.now();
  log('info', 'Fetching assignments');
  try {
    await store.dispatch('conversationAssignments/fetch', conversationKey.value);
    log('info', 'Assignments fetched', {
      count: assignments.value.length,
      isInSync: isInSync.value,
      fetchTimeMs: Math.round(performance.now() - start),
    });
  } catch (error) {
    const status = error?.response?.status;
    if ([401, 403, 404].includes(status)) {
      // Expected when the conversation was reassigned and current agent no longer has access (RLS).
      store.commit('conversationAssignments/setAssignments', {
        conversationId: conversationKey.value,
        payload: [],
        meta: {},
      });
      syncRecoveryAttempts.value = MAX_SYNC_RECOVERY_ATTEMPTS + 1;
      log('info', 'Assignments unavailable for current user', { status });
      return;
    }

    // Keep widget non-blocking if endpoint is temporarily unavailable.
    log('error', 'Fetch failed', {
      status,
      data: error?.response?.data,
      message: error?.message,
    });
  }
};

const onConversationUpdated = payload => {
  const payloadConversationId = Number(
    payload?.id || payload?.conversation_id || payload?.conversation?.id
  );

  log('debug', 'Realtime event received', {
    eventConversationId: payloadConversationId,
    payloadAssigneeId: payload?.assignee_id || payload?.meta?.assignee?.id,
    isMatch: payloadConversationId === conversationKey.value,
  });

  if (!payloadConversationId || payloadConversationId !== conversationKey.value) {
    return;
  }

  setTimeout(() => fetchAssignments(), 300);
};

watch(
  () => props.conversationId,
  () => {
    syncRecoveryAttempts.value = 0;
    fetchAssignments();
  },
  { immediate: true }
);

watch(
  () => props.assigneeId,
  () => {
    log('info', 'assigneeId prop changed');
    fetchAssignments();
  }
);

watch(isInSync, synced => {
  if (synced) {
    syncRecoveryAttempts.value = 0;
    return;
  }

  if (uiFlags.value?.isFetching) {
    return;
  }

  if (syncRecoveryAttempts.value >= MAX_SYNC_RECOVERY_ATTEMPTS) {
    log('warn', 'Max sync attempts reached', {
      widgetAssigneeId: currentAssignment.value?.assignee?.id,
    });
    return;
  }

  syncRecoveryAttempts.value += 1;
  log('warn', 'Desynchronized, forcing refresh', {
    widgetAssigneeId: currentAssignment.value?.assignee?.id,
    attempt: syncRecoveryAttempts.value,
  });
  setTimeout(() => fetchAssignments(), 500);
});

const getAssignmentDescription = assignment => {
  const toAssigneeName = assignment?.assignee?.name || unassignedLabel.value;
  const previousAssigneeName = assignment?.previous_assignee?.name;
  const assignedByName = assignment?.assigned_by?.name;

  if (assignment?.assignment_type === 'manual' && assignedByName) {
    if (previousAssigneeName) {
      return `${assignedByName} atribuiu de ${previousAssigneeName} para ${toAssigneeName}`;
    }
    return `${assignedByName} atribuiu para ${toAssigneeName}`;
  }

  if (assignment?.assignment_type === 'auto_redistribution') {
    const timeoutText = assignment?.timeout_seconds
      ? ` - ${formatDuration(assignment.timeout_seconds)} sem resposta`
      : '';
    if (previousAssigneeName) {
      return `${systemLabel.value} redistribuiu de ${previousAssigneeName} para ${toAssigneeName}${timeoutText}`;
    }
    return `Redistribuicao automatica${timeoutText}`;
  }

  if (assignment?.assignment_type === 'ai_transfer') {
    return 'Transferido pela IA';
  }

  if (assignment?.assignment_type === 'round_robin') {
    return 'Distribuicao automatica (round robin)';
  }

  if (assignment?.assignment_type === 'bot_handoff') {
    return 'Handoff do bot';
  }

  return formatType(assignment?.assignment_type);
};

const toggleExpanded = () => {
  expanded.value = !expanded.value;
};

onMounted(() => {
  emitter.on('conversation.updated', onConversationUpdated);
  emitter.on('conversation:updated', onConversationUpdated);
  emitter.on('assignee.changed', onConversationUpdated);
  emitter.on('assignee:changed', onConversationUpdated);
});

onBeforeUnmount(() => {
  emitter.off('conversation.updated', onConversationUpdated);
  emitter.off('conversation:updated', onConversationUpdated);
  emitter.off('assignee.changed', onConversationUpdated);
  emitter.off('assignee:changed', onConversationUpdated);
});
</script>

<template>
  <div class="assignment-history-widget border border-n-weak rounded-lg bg-n-solid-2 mx-3 mb-2">
    <button
      type="button"
      class="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-n-alpha-2"
      @click="toggleExpanded"
    >
      <fluent-icon icon="arrow-clockwise" size="14" class="text-n-slate-11" />
      <span class="text-xs font-medium text-n-slate-12">
        {{ t('CONVERSATION.ASSIGNMENT_HISTORY.TITLE') }}
      </span>
      <span class="text-xs text-n-slate-11">
        ({{ meta.total_count || assignments.length }})
      </span>
      <span class="ml-auto text-xs text-n-slate-11 truncate max-w-[10rem]">
        {{
          effectiveCurrentAssignment?.assignee?.name ||
          unassignedLabel
        }}
      </span>
      <fluent-icon
        icon="chevron-down"
        size="12"
        class="text-n-slate-11 transition-transform duration-200"
        :class="{ 'rotate-180': expanded }"
      />
    </button>

    <div v-if="expanded" class="border-t border-n-weak px-3 py-2">
      <div v-if="shouldShowDesyncWarning" class="mb-2 text-xs text-ruby-11">
        Widget temporariamente dessincronizado. Atualizando...
      </div>
      <div v-if="meta.auto_redistributions > 0" class="mb-2 text-xs text-n-slate-11">
        {{ t('CONVERSATION.ASSIGNMENT_HISTORY.AUTO_REDISTRIBUTIONS') }} {{ meta.auto_redistributions }}
        <span v-if="meta.avg_timeout_seconds">
          - {{ t('CONVERSATION.ASSIGNMENT_HISTORY.AVERAGE') }}
          {{ formatDuration(meta.avg_timeout_seconds) }}
        </span>
      </div>

      <div
        v-if="uiFlags.isFetching"
        class="text-xs text-n-slate-11 px-1 py-2"
      >
        {{ t('CONVERSATION.ASSIGNMENT_HISTORY.LOADING') }}
      </div>

      <div
        v-else-if="hasAssignments"
        class="flex flex-col gap-2 max-h-56 overflow-y-auto pr-1"
      >
        <div
          v-for="(assignment, index) in assignments"
          :key="assignment.id"
          class="rounded-md border border-n-weak p-2 bg-n-alpha-1"
          :class="{ 'border-l-2 border-l-emerald-11': index === 0 }"
        >
          <div class="flex items-center gap-2">
            <Avatar
              :name="assignment.assignee?.name || 'N/A'"
              :src="assignment.assignee?.avatar_url"
              :size="20"
              rounded-full
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{
                assignment.assignee?.name ||
                unassignedLabel
              }}
            </span>
            <span
              v-if="index === 0"
              class="text-[10px] bg-emerald-9 text-white rounded px-1.5 py-0.5"
            >
              {{ t('CONVERSATION.ASSIGNMENT_HISTORY.CURRENT') }}
            </span>
          </div>

          <div class="text-xs text-n-slate-11 mt-1">
            {{ formatTimestamp(assignment.created_at) }} -
            {{ formatType(assignment.assignment_type) }}
          </div>

          <div class="text-xs text-n-slate-11 mt-1">
            {{ getAssignmentDescription(assignment) }}
          </div>

          <div
            v-if="assignment.timeout_seconds && index !== 0"
            class="text-xs text-ruby-11 mt-1"
          >
            {{ formatDuration(assignment.timeout_seconds) }}
            {{ t('CONVERSATION.ASSIGNMENT_HISTORY.WAITING_WITHOUT_RESPONSE') }}
          </div>
        </div>
      </div>

      <div
        v-else
        class="text-xs text-n-slate-11 px-1 py-2"
      >
        {{ t('CONVERSATION.ASSIGNMENT_HISTORY.EMPTY') }}
      </div>
    </div>
  </div>
</template>

